/**
 * NtfyNotificationService
 * 
 * Firebase-free push notification service for Web using ntfy.sh
 * Uses Server-Sent Events (SSE) for real-time notifications
 * 
 * Features:
 * - No Firebase dependency
 * - Real-time SSE connection
 * - Browser Notifications API integration
 * - Automatic reconnection with exponential backoff
 * - In-app toast notifications
 */

import { createClient } from '@/lib/supabase/client'

const NTFY_SERVER = 'https://ntfy.sh'
const INITIAL_RETRY_DELAY = 1000
const MAX_RETRY_DELAY = 60000
const RETRY_MULTIPLIER = 2

export type ConnectionState =
    | { status: 'disconnected' }
    | { status: 'connecting' }
    | { status: 'connected'; topic: string }
    | { status: 'error'; message: string }

export interface NtfyMessage {
    id: string
    time: number
    event?: string
    topic: string
    title?: string
    message?: string
    priority: number
    tags?: string[]
    click?: string
    actions?: NtfyAction[]
    extras?: Record<string, string>
}

export interface NtfyAction {
    action: string
    label: string
    url?: string
    clear?: boolean
}

type NotificationHandler = (message: NtfyMessage) => void

class NtfyNotificationService {
    private eventSource: EventSource | null = null
    private currentTopic: string | null = null
    private retryCount = 0
    private isActive = false
    private connectionState: ConnectionState = { status: 'disconnected' }
    private handlers: NotificationHandler[] = []

    /**
     * Subscribe to notification events
     */
    onNotification(handler: NotificationHandler): () => void {
        this.handlers.push(handler)
        return () => {
            this.handlers = this.handlers.filter(h => h !== handler)
        }
    }

    /**
     * Get current connection state
     */
    getConnectionState(): ConnectionState {
        return this.connectionState
    }

    /**
     * Start listening for notifications
     */
    async start(): Promise<void> {
        if (this.isActive) return

        try {
            const topic = await this.getUserTopic()
            if (topic) {
                this.isActive = true
                this.connect(topic)

                // Request browser notification permission
                if ('Notification' in window && Notification.permission === 'default') {
                    await Notification.requestPermission()
                }
            } else {
                console.warn('[ntfy] No topic found for user')
                this.connectionState = { status: 'error', message: 'No notification topic configured' }
            }
        } catch (error) {
            console.error('[ntfy] Failed to start:', error)
            this.connectionState = {
                status: 'error',
                message: error instanceof Error ? error.message : 'Unknown error'
            }
        }
    }

    /**
     * Stop listening for notifications
     */
    stop(): void {
        this.isActive = false
        this.eventSource?.close()
        this.eventSource = null
        this.currentTopic = null
        this.connectionState = { status: 'disconnected' }
    }

    /**
     * Check if service is active
     */
    isConnected(): boolean {
        return this.isActive && this.eventSource !== null &&
            this.eventSource.readyState === EventSource.OPEN
    }

    // Private methods

    private async getUserTopic(): Promise<string | null> {
        try {
            const supabase = await createClient()
            const { data: { user } } = await supabase.auth.getUser()

            if (!user) return null

            const { data, error } = await supabase
                .from('user_notification_preferences')
                .select('ntfy_topic')
                .eq('user_id', user.id)
                .single()

            if (error) {
                // If no preferences exist, create them
                if (error.code === 'PGRST116') {
                    const { data: rpcData } = await supabase
                        .rpc('get_user_ntfy_topic', { p_user_id: user.id })
                    return rpcData as string
                }
                throw error
            }

            return data?.ntfy_topic ?? null
        } catch (error) {
            console.error('[ntfy] Failed to get user topic:', error)
            return null
        }
    }

    private connect(topic: string): void {
        if (!this.isActive) return

        // Close existing connection
        this.eventSource?.close()

        this.currentTopic = topic
        this.connectionState = { status: 'connecting' }

        const url = `${NTFY_SERVER}/${topic}/sse`
        console.log('[ntfy] Connecting to:', url)

        this.eventSource = new EventSource(url)

        this.eventSource.onopen = () => {
            console.log('[ntfy] Connected to topic:', topic)
            this.retryCount = 0
            this.connectionState = { status: 'connected', topic }
        }

        this.eventSource.onmessage = (event) => {
            this.handleMessage(event.data)
        }

        this.eventSource.onerror = () => {
            if (window.location.hostname !== 'localhost') {
                console.error('[ntfy] Connection error')
            }
            this.connectionState = { status: 'error', message: 'Connection failed' }
            this.eventSource?.close()
            this.scheduleReconnect(topic)
        }
    }

    private scheduleReconnect(topic: string): void {
        if (!this.isActive) return

        const delay = this.calculateRetryDelay()
        console.log(`[ntfy] Reconnecting in ${delay}ms (attempt ${this.retryCount + 1})`)

        setTimeout(() => {
            if (this.isActive) {
                this.retryCount++
                this.connect(topic)
            }
        }, delay)
    }

    private calculateRetryDelay(): number {
        const delay = INITIAL_RETRY_DELAY * Math.pow(RETRY_MULTIPLIER, this.retryCount)
        return Math.min(delay, MAX_RETRY_DELAY)
    }

    private handleMessage(data: string): void {
        try {
            const message: NtfyMessage = JSON.parse(data)

            // Skip system events
            if (message.event === 'keepalive' || message.event === 'open') {
                return
            }

            console.log('[ntfy] Received:', message.title)

            // Emit to handlers
            this.handlers.forEach(handler => handler(message))

            // Show browser notification if not focused
            if (!document.hasFocus()) {
                this.showBrowserNotification(message)
            }

        } catch (error) {
            console.error('[ntfy] Failed to parse message:', error)
        }
    }

    private showBrowserNotification(message: NtfyMessage): void {
        if (!('Notification' in window) || Notification.permission !== 'granted') {
            return
        }

        const notification = new Notification(message.title ?? 'LinkBox', {
            body: message.message,
            icon: '/icons/icon-192x192.png',
            tag: message.id,
            data: {
                click: message.click,
                type: message.extras?.['X-Notification-Type']
            }
        })

        notification.onclick = () => {
            window.focus()

            // Navigate based on notification type
            const type = message.extras?.['X-Notification-Type']
            if (type === 'card_update_request' || type === 'transaction_alert') {
                window.location.href = '/your-linkbox/finance?tab=vendor-cards'
            } else if (message.click) {
                // Handle linkbox:// deep links
                const url = message.click.replace('linkbox://', '/your-linkbox/')
                window.location.href = url
            }

            notification.close()
        }
    }
}

// Singleton instance
export const ntfyService = new NtfyNotificationService()

// Export for use in components
export default NtfyNotificationService
